import {Component, Input, OnChanges, SimpleChanges, inject, Output, EventEmitter, OnInit, ChangeDetectorRef} from '@angular/core';
import {CommonModule, NgTemplateOutlet} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {DialogModule} from 'primeng/dialog';
import {ButtonModule} from 'primeng/button';
import {WeatherInfoTabComponent} from './tabs/weather-info-tab/weather-info-tab.component';
import {
  WeatherGraphTabComponent
} from '@app/pages/weather/pages/gis-weather/components/point-polygon-details-modal/tabs/weather-graph-tab/weather-graph-tab.component';
import {WeatherSummaryTabComponent} from './tabs/weather-summary-tab/weather-summary-tab.component';
import {of} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {MapMonitoringObject, MapMonitoringObjectService} from '@6grain/masofa-monolith-api-client/map';
import {MapMonitoringObjectType} from '@6grain/masofa-monolith-api-client/map/model/mapMonitoringObjectType';
import {PolygonValidationService} from '../../services/polygon-validation.service';
import {MessageService} from 'primeng/api';
import {Loading} from '@components/ui/loading/loading';
import {RegionManagerService} from '@components/openstreetmap/managers/region-manager.service';
import {OldMapComponent} from '@components/old-map/old-map.component';
import {PolygonConverterUtil} from '@app/utils/polygon-converter.util';
import {PrimaryButton} from '@components/button/primary-button/primary-button';
import {SecondaryButton} from '@components/button/secondary-button/secondary-button';

export type PointPolygonTab = 'info' | 'graph' | 'summary' | 'contour';

@Component({
  selector: 'app-point-polygon-details-modal',
  standalone: true,
  imports: [
    CommonModule,
    NgTemplateOutlet,
    FormsModule,
    TranslateModule,
    DialogModule,
    ButtonModule,
    WeatherInfoTabComponent,
    WeatherGraphTabComponent,
    WeatherSummaryTabComponent,
    Loading,
    OldMapComponent,
    PrimaryButton,
    SecondaryButton
  ],
  templateUrl: './point-polygon-details-modal.component.html',
  styleUrls: ['./point-polygon-details-modal.component.scss']
})
export class PointPolygonDetailsModalComponent implements OnChanges, OnInit {
  private mapMonitoringObjectService = inject(MapMonitoringObjectService);
  private polygonValidationService = inject(PolygonValidationService);
  private messageService = inject(MessageService);
  private translate = inject(TranslateService);
  private regionManagementManager = inject(RegionManagerService);
  private readonly cdr = inject(ChangeDetectorRef);

  public polygonJsonWktInputFormat: string = '';
  public polygonJsonWktOutputFormat: string = '';

  public canSave: boolean = true;
  public countryPolygon: { id: string; coordinates: Array<[number, number]> }[] = [];


  // Required Inputs
  @Input() visible: boolean = false;
  /** When true, render body inline (no p-dialog) for embedding in another surface (e.g. field details tab). */
  @Input() embedded = false;
  @Input() latitude: number | null = null;
  @Input() longitude: number | null = null;

  // Optional Inputs
  @Input() pointName?: string;
  @Input() pointData?: any;
  @Input() isOnlyInfo = false;
  @Input() regionPathString?: string | null | undefined;
  @Input() date: any = new Date();
  @Input() dataSourceOptions = [
    {
      names: {
        'en-US': 'ERA5',
        'fr-FR': 'ERA5',
        'ru-RU': 'ERA5',
        'uz-Latn-UZ': 'ERA5',
        'ar-LB': 'ERA5',
        'ar-SA': 'ERA5',
      },
      id: 'era5',
    },
    {
      names: {
        'en-US': 'IBM',
        'fr-FR': 'IBM',
        'ru-RU': 'IBM',
        'uz-Latn-UZ': 'IBM',
        'ar-LB': 'IBM',
        'ar-SA': 'IBM',
      },
      id: 'ibm',
    },
    {
      names: {
        'en-US': 'UGM',
        'fr-FR': 'UGM',
        'ru-RU': 'УГМ',
        'uz-Latn-UZ': 'UGM',
        'ar-LB': 'UGM',
        'ar-SA': 'UGM',
      },
      id: 'ugm',
    },
  ];

  // Outputs
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() closed = new EventEmitter<void>();
  @Output() addPoint = new EventEmitter<{
    latitude: number;
    longitude: number;
    name?: string;
    result?: MapMonitoringObject;
  }>();
  @Output() export = new EventEmitter<{ latitude: number, longitude: number } | void>();

  // Tab Management
  activeTab: PointPolygonTab = 'info';

  weatherStationName: string | undefined = undefined;
  isLoadingStation: boolean = false;
  isAddingPoint: boolean = false;


  ngOnChanges(changes: SimpleChanges): void {
    if (changes['embedded']?.currentValue === true && this.activeTab === 'contour') {
      this.activeTab = 'info';
    }

    if (changes['visible'] && this.visible &&
      this.latitude !== null && this.longitude !== null) {
    }

    if ((changes['latitude'] || changes['longitude']) &&
      this.latitude !== null && this.longitude !== null) {
    }

    if ((changes['visible'] && this.visible) || (this.visible && (changes['pointData'] || changes['pointName']))) {
      this.updatePointName();
      if (this.pointData?.polygonJson) {
        this.polygonJsonWktInputFormat = PolygonConverterUtil.coordinatesLatLonToWKT(this.pointData.polygonJson);
        this.polygonJsonWktOutputFormat = JSON.parse(JSON.stringify(this.polygonJsonWktInputFormat));
      }
      this.cdr.markForCheck();
    }
  }

  ngOnInit(): void {
    const regionCache = this.regionManagementManager.getRegionPolygonsCache();
    this.countryPolygon = Array.from(regionCache.values()).map(entry => ({
      id: entry.polygon.id,
      coordinates: entry.polygon.coordinates,
    }));
  }

  private updatePointName(): void {
    if (this.pointData?.name && this.pointData.name !== '') {
      this.weatherStationName = this.pointData.name;
    } else if (this.pointName && this.pointName !== '') {
      this.weatherStationName = this.pointName;
    } else {
      this.weatherStationName = this.regionPathString as string;
    }
  }

  pointNameChanged(): void {
    if (this.pointName === undefined || this.pointName === '') {
      this.updatePointName();
    } else {
      this.weatherStationName = this.pointName;
    }
  }

  get pointNamePlaceholderKey(): string | any {
    return (this.pointData?.polygonJson && Array.isArray(this.pointData.polygonJson) && this.pointData.polygonJson.length > 0)
      ? 'ENTER_POLYGON_NAME'
      : 'ENTER_POINT_NAME';
  }

  /** Contour edit tab (map + geometry): not shown when embedded (e.g. field details Meteo tab). */
  get showContourTab(): boolean {
    return (
      !this.embedded &&
      !!this.pointData?.polygonJson &&
      Array.isArray(this.pointData.polygonJson) &&
      this.pointData.polygonJson.length > 0
    );
  }

  /**
   * Handle tab change
   */
  onTabChange(tab: PointPolygonTab): void {
    this.activeTab = tab;
    this.cdr.markForCheck();
  }

  /**
   * Close modal
   */
  onClose(): void {
    if (this.embedded) {
      this.resetData();
      return;
    }
    this.visible = false;
    this.visibleChange.emit(false);
    this.closed.emit();
    this.resetData();
  }

  /**
   * Reset all data
   */
  private resetData(): void {
    this.weatherStationName = undefined;
    this.activeTab = 'info';
  }

  /**
   * Add point to favorites or list
   */
  onAddPoint(): void {
    if (this.isAddingPoint) {
      return;
    }
    if (this.latitude !== null && this.longitude !== null) {
      this.isAddingPoint = true;
      this.cdr.markForCheck();
      // Validate point is within country boundaries using region cache
      const regionCache = this.regionManagementManager.getRegionPolygonsCache();


      let type: MapMonitoringObjectType = MapMonitoringObjectType.NUMBER_2;
      if (this.pointData?.polygonJson && Array.isArray(this.pointData.polygonJson) && this.pointData.polygonJson.length > 0) {
        type = MapMonitoringObjectType.NUMBER_1;
      }

      if (regionCache && regionCache.size > 0) {
        const point: [number, number] = [this.latitude, this.longitude];
        // Convert Map values to array of polygons
        const countryPolygons = Array.from(regionCache.values()).map(entry => ({
          id: entry.polygon.id,
          coordinates: entry.polygon.coordinates
        }));

        let inside: boolean;
        let _errorMessageKey = 'ERROR_POINT_OUTSIDE_BOUNDARIES';
        if (type == MapMonitoringObjectType.NUMBER_1) {
          _errorMessageKey = 'ERROR_POLYGON_OUTSIDE_BOUNDARIES';
          // For polygons, validate entire polygon
          const polygonCoordinates: [number, number][] = PolygonConverterUtil.polygonJsonToCoordinates(this.polygonJsonWktOutputFormat);
          inside = this.polygonValidationService.isPolygonWithinCountry(polygonCoordinates, countryPolygons as any);
        } else {
          inside = this.polygonValidationService.isPointWithinCountry(point, countryPolygons as any);
        }
        if (!inside) {
          const errorMessage = this.translate.instant(_errorMessageKey);
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('ERROR'),
            detail: errorMessage,
            life: 5000
          });
          this.isAddingPoint = false;
          this.cdr.markForCheck();
          return;
        }
      }



      // Prepare polygonJson
      let polygonJson = this.pointData?.polygonJson || [];

      if (this.polygonJsonWktOutputFormat) {
        // Convert WKT to coordinates format
        polygonJson = PolygonConverterUtil.wktToCoordinatesLatLon(this.polygonJsonWktOutputFormat)
      }

      // Prepare the monitoring object data
      const monitoringObject: MapMonitoringObject = {
        name: this.weatherStationName || this.regionPathString || 'Weather Point',
        type,
        pointJson: {
          lat: this.latitude,
          lon: this.longitude
        },
        polygonJson: polygonJson
      };

      // Add regionId using type assertion
      (monitoringObject as any).regionId = this.pointData?.region?.id || (this.pointData as any)?.regionId || null;


      if (monitoringObject.polygonJson?.length === 0) {
        delete monitoringObject.polygonJson;
      } else {
        delete monitoringObject.pointJson;
      }


      // Check if we're editing or creating
      if (this.pointData?.id) {
        // Update existing monitoring point
        monitoringObject.id = this.pointData.id;
        this.mapMonitoringObjectService.tilesMapMonitoringObjectUpdatePut(monitoringObject)
          .pipe(
            catchError(error => {
              console.error('Error updating monitoring point:', error);
              this.isAddingPoint = false;
              this.cdr.markForCheck();
              return of(null);
            })
          )
          .subscribe(result => {
            this.isAddingPoint = false;
            if (result) {
              this.addPoint.emit({
                latitude: this.latitude!,
                longitude: this.longitude!,
                name: this.weatherStationName || undefined,
                result
              });
              if (!this.embedded) {
                this.visible = false;
              }
              this.pointData = null;
            }
            this.cdr.markForCheck();
          });
      } else {
        // Create new monitoring point
        this.mapMonitoringObjectService.tilesMapMonitoringObjectCreatePost(monitoringObject)
          .pipe(
            catchError(error => {
              console.error('Error creating monitoring point:', error);
              this.isAddingPoint = false;
              this.cdr.markForCheck();
              return of(null);
            })
          )
          .subscribe(createdId => {
            this.isAddingPoint = false;
            if (createdId != null) {
              const createdResult = { ...monitoringObject, id: createdId } as MapMonitoringObject;
              this.addPoint.emit({
                latitude: this.latitude!,
                longitude: this.longitude!,
                name: this.weatherStationName || undefined,
                result: createdResult
              });
              if (!this.embedded) {
                this.visible = false;
              }
              this.pointData = null;
            }
            this.cdr.markForCheck();
          });
      }
    }
  }

  onExport(): void {
    if (this.latitude !== null && this.longitude !== null) {
      this.export.emit({latitude: this.latitude, longitude: this.longitude});
    } else {
      this.export.emit();
    }
  }

  /**
   * Calculate and format polygon area
   * @returns Formatted area string with unit (hectares) or null if no polygon
   */
  get polygonArea(): string | null {
    if (!this.pointData?.polygonJson || !Array.isArray(this.pointData.polygonJson) || this.pointData.polygonJson.length === 0) {
      return null;
    }

    try {
      // Convert polygonJson to coordinates format [lat, lng][]
      const coordinates: [number, number][] = this.pointData.polygonJson.map((point: any) => [
        point.lat || 0,
        point.lon || 0
      ]);

      // Use the utility method to calculate area in hectares
      return PolygonConverterUtil.calculatePolygonAreaInHectares(coordinates);
    } catch (error) {
      console.error('Error calculating polygon area:', error);
      return null;
    }
  }

  checkCanSave($event: any) {

    const polygonCoords  = PolygonConverterUtil.polygonJsonToCoordinates($event);
    const inside = this.polygonValidationService.isPolygonWithinCountry(polygonCoords, this.countryPolygon as any);

    const _errorMessageKey = 'ERROR_POLYGON_OUTSIDE_BOUNDARIES';

    if (!inside) {
      setTimeout(() => {
        this.canSave = false;
        this.cdr.markForCheck();
      }, 100);

      this.canSave = false;
      const errorMessage = this.translate.instant(_errorMessageKey);
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('ERROR'),
        detail: errorMessage,
        life: 5000
      });
      this.cdr.markForCheck();
      return;
    }else {
      this.canSave = true;
    }
    this.cdr.markForCheck();
  }
}
